import "assets/css/gridstack.css";
import { useCspNonce } from "context/CspContext";
import {
    GridItemHTMLElement,
    GridStack,
    GridStackNode,
    GridStackOptions,
} from "gridstack";
import { debounce, sortBy } from "lodash";
import React, { useRef, useState } from "react";

const CELL_HEIGHT = 4;
const GAP = 8;
const GRIDSTACK_COLUMNS = 12; // 12 columns in gridstack
const STACK_GAP = 10;

export const pixelToGridUnits = (px: number, wrapperWidth: number) => {
    const pxPerUnit = wrapperWidth / GRIDSTACK_COLUMNS;
    return Math.ceil(px / pxPerUnit);
};

const loadGridStack = (
    element: string,
    options: {
        [key: string]: any;
        onChange?: (result: GridStackChangeEvent) => void;
    } = {}
): Promise<GridStack> => {
    return new Promise((resolve) => {
        const ele = document?.querySelector(element) as any;

        const loaded = setInterval(() => {
            if (ele) {
                clearInterval(loaded);
                const gridGap = options?.gap ?? GAP;
                delete options?.gap;
                const reRender = options?.reRender;
                delete options?.reRender;
                options.cellHeight = options?.cellHeight || CELL_HEIGHT + "px";
                options.margin = gridGap / 2;

                const gridOptions: GridStackOptions = {
                    resizable: {},
                    removable: false,
                    disableResize: true,
                    ...options,
                };

                const setGsHeight = (ele: HTMLElement) => {
                    if (!ele.getAttribute("gs-h") && reRender !== false) {
                        let gsH = Math.ceil(
                            (ele.offsetHeight + gridGap) /
                            Number.parseInt(
                                options.cellHeight || CELL_HEIGHT
                            )
                        );

                        if (options.setItemHeight) {
                            gsH =
                                options.setItemHeight(ele, options) /
                                CELL_HEIGHT;
                        }

                        ele.setAttribute("gs-h", String(gsH));
                        const childEle = ele.firstElementChild as HTMLElement;
                        childEle.style.height = `${gsH *
                            Number.parseInt(
                                options.cellHeight || CELL_HEIGHT
                            ) -
                            gridGap
                            }px`;
                    }
                };

                ele?.querySelectorAll(".grid-stack-item").forEach(setGsHeight);
                let grid: GridStack;
                try {
                    grid = GridStack.addGrid(ele, gridOptions);
                } catch {
                    return;
                }
                // Root cause:
                // GridStack `_dragOrResize` reads `node._lastUiPosition.left` on `drag`.
                // If a widget is dragged outside the valid area, `_lastUiPosition` can be
                // undefined for one tick, causing:
                // "Cannot read properties of undefined (reading 'left')".
                // Catch that specific edge-case so the UI doesn't crash.
                const dragOrResize = (grid as any)._dragOrResize?.bind(grid);
                if (typeof dragOrResize === "function") {
                    (grid as any)._dragOrResize = (...args: any[]) => {
                        try {
                            return dragOrResize(...args);
                        } catch (error: any) {
                            const message = String(error?.message || "");
                            if (
                                message.includes(
                                    "Cannot read properties of undefined (reading 'left')"
                                )
                            ) {
                                return;
                            }
                            throw error;
                        }
                    };
                }

                const elementSorting = () => {
                    const data: GridStackDataProps[] = [];
                    const elementList: {
                        [key: number]: HTMLElement;
                    } = {};

                    for (const item of ele.querySelectorAll?.(
                        ".grid-stack-item"
                    ) || []) {
                        elementList[Number(item.getAttribute("gs-y"))] =
                            item as HTMLElement;
                        data.push({
                            id: item.id,
                            isWarning: Boolean(item.getAttribute("gs-warning")),
                            w: Number(item.getAttribute("gs-w")),
                            h: Number(item.getAttribute("gs-h")),
                            x: Number(item.getAttribute("gs-x")),
                            y: Number(item.getAttribute("gs-y")),
                        });
                    }

                    // Re-render element sorted
                    if (reRender !== false) {
                        ele.innerHTML = "";
                        for (const item of Object.values(elementList)) {
                            ele.appendChild(item);
                        }
                    }

                    return data;
                };

                const gridChangeHandler = debounce(
                    (event: Event, items?: GridStackNode[]) => {
                        options?.onChange?.({
                            data: elementSorting(),
                            event,
                            items,
                            gridstack: grid,
                        });
                    },
                    200
                );

                // The item has been dragged within the grid
                grid.on("dragstop", (event: Event, el: GridItemHTMLElement) => {
                    if (!el?.gridstackNode) {
                        return;
                    }

                    const node = el.gridstackNode;
                    const nodes = node ? [node] : [];
                    return gridChangeHandler(event, nodes);
                });

                // The item has been added to the grid (from outside)
                grid.on("dropped", (event: Event, prevNode, newNode) => {
                    const nodes = newNode ? [newNode] : [];
                    return gridChangeHandler(event, nodes);
                });

                grid.on("removed", (event: Event, items: GridStackNode[]) => {
                    return gridChangeHandler(event, items);
                });

                // The item has been resized manually
                grid.on(
                    "resizestop",
                    (event: Event, el: GridItemHTMLElement) => {
                        if (!el?.gridstackNode) {
                            return;
                        }

                        const node = el.gridstackNode;
                        const nodes = node ? [node] : [];
                        return gridChangeHandler(event, nodes);
                    }
                );

                resolve(grid);
            }
        }, 10);
    });
};

type GridStackDataProps = {
    id: number | string;
    w: number;
    h: number;
    x: number;
    y: number;
    [key: string]: any;
};

export type GridStackChangeEvent = {
    data: GridStackDataProps[];
    event: Event;
    items?: GridStackNode[];
    gridstack: GridStack;
};

type GridStackElementProps = {
    id: string;
    itemCount: number;
    cellHeight?: number;
    gap?: number;
    options?: { [key: string]: any };
    setItemHeight?: (element: HTMLElement, options: any) => void;
    onChange?: (result: GridStackChangeEvent) => void;
    onResizeStop?: (event: Event, items?: any) => void;
    onInit?: (grid: GridStack) => void;
    items?: any[];
    [key: string]: any;
};

const GridStackElement: React.FunctionComponent<GridStackElementProps> = (
    props
) => {
    const {
        id,
        itemCount,
        cellHeight,
        gap,
        options,
        setItemHeight,
        onChange,
        children,
        className,
        reRender,
        onResizeStop,
        onInit,
        items,
        ...rest
    } = props;

    const [grid, setGrid] = useState<GridStack>();
    const [numberItems, setNumberItems] = useState<number>(0);
    const gridInstance = useRef<any>(null);

    const { nonce } = useCspNonce();

    React.useEffect(() => {
        if (grid && options?.disableDrag !== undefined) {
            // Update draggable state
            const items = grid.getGridItems();
            items.forEach((item) => {
                grid.movable(item, !options.disableDrag);
            });
        }
    }, [grid, options?.disableDrag]);

    React.useEffect(() => {
        if (items?.length && gridInstance.current) {
            gridInstance.current.batchUpdate();
            gridInstance.current.removeAll(false);
            const rowHeight = 1;
            gridInstance.current.cellHeight(rowHeight);
            const sortedItems = sortBy(items, "order");
            let previousH = 0;
            sortedItems.forEach(({ uuid, type_ }) => {
                const element = document.getElementById(`gs-${uuid}`);
                if (element) {
                    const childElement: any =
                        element.querySelector(".action-item");
                    let childHeight = childElement.offsetHeight;

                    if (type_ === "WARNING") {
                        childHeight = "173";
                        childElement.style.height = `${childHeight}px`;
                    }

                    const gridStackItemHeight =
                        rowHeight * Number(childHeight) + STACK_GAP;
                    // gs-h attribute determines the overall height of the grid item in terms of rows
                    element.setAttribute("gs-h", gridStackItemHeight + "");

                    element.setAttribute("gs-y", previousH.toString());
                    gridInstance.current.makeWidget(element);
                    previousH += Number(
                        rowHeight * Number(childHeight) + STACK_GAP
                    );
                }
            });
            gridInstance.current.batchUpdate(false);
        }
    }, [items]);

    React.useEffect(() => {
        let isMounted = true;

        if (numberItems !== itemCount && !items) {
            grid?.destroy(false);
            setNumberItems(itemCount);
        }

        if (id && itemCount > 0) {
            loadGridStack("#" + id, {
                ...options,
                reRender,
                cellHeight,
                gap,
                onChange,
                onResizeStop,
                setItemHeight,
                nonce,
            }).then((grid) => {
                if (isMounted) {
                    setGrid(grid);
                    gridInstance.current = grid;
                    const items = grid.getGridItems();
                    items.forEach((item) => {
                        grid.movable(item, !options?.disableDrag);
                        grid.opts.disableDrag = options?.disableDrag;
                    });
                    if (typeof onInit === "function") {
                        onInit(grid);
                    }
                }
            });
        }

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, itemCount, className]);

    React.useEffect(() => {
        if (grid && options?.disableDrag !== undefined) {
            // Update draggable state
            const items = grid.getGridItems();
            grid.batchUpdate();
            grid.removeAll(false);
            items.forEach((item) => {
                grid.movable(item, !options.disableDrag);
                grid.opts.disableDrag = options.disableDrag;
                grid.makeWidget(item);
            });
            grid.batchUpdate(false);
        }
    }, [grid, options?.disableDrag]);

    return (
        <div id={id} className={`grid-stack ${className || ""}`} {...rest}>
            {children}
        </div>
    );
};

// By old dashboard panel size logic
export const getItemWidth = (screenWidth: number, defaultWidth?: number) => {
    let columns: 1 | 2 | 3 | 4 = 1;

    if (screenWidth > 1800) {
        columns = 4;
    } else if (screenWidth > 1400) {
        columns = 3;
    } else if (screenWidth > 768) {
        columns = 2;
    }

    const itemWidth = GRIDSTACK_COLUMNS / columns;

    return defaultWidth ? Math.max(itemWidth, defaultWidth) : itemWidth;
};

export default GridStackElement;
